import React, { useState, useEffect } from 'react';
import { X, UserPlus, Eye, Edit, Trash2 } from 'lucide-react';
import { shareSheet } from '../services/SheetStorageService';

const ShareModal = ({ isOpen, onClose, sheet, onShareUpdate }) => {
  const [username, setUsername] = useState('');
  const [permission, setPermission] = useState('view');
  const [sharedUsers, setSharedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (sheet && sheet.sharedWith) {
      // Ensure sharedWith is an array and has proper structure
      const users = Array.isArray(sheet.sharedWith) ? sheet.sharedWith : [];
      setSharedUsers(users);
    }
  }, [sheet]);

  const handleShare = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    // Check if username starts with @
    const cleanUsername = username.startsWith('@') ? username.slice(1) : username;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updatedSheet = await shareSheet(sheet.id || sheet._id, cleanUsername, permission);
      
      if (updatedSheet) {
        setSuccess(`Sheet shared with @${cleanUsername}`);
        setUsername('');
        setSharedUsers(updatedSheet.sharedWith || []);
        
        // Notify parent component
        if (onShareUpdate) {
          onShareUpdate(updatedSheet);
        }
      } else {
        setError('Failed to share sheet. User may not exist.');
      }
    } catch (err) {
      setError(err.message || 'Failed to share sheet');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveShare = async (userId) => {
    // This would need a new API endpoint to remove sharing
    // For now, we can update with 'none' permission or implement later
    console.log('Remove share for user:', userId);
    setError('Remove sharing feature coming soon');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Share Sheet</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Share form */}
          <form onSubmit={handleShare} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Share with user
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="@username"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
                <select
                  value={permission}
                  onChange={(e) => setPermission(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                >
                  <option value="view">Can view</option>
                  <option value="edit">Can edit</option>
                </select>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <UserPlus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
                {success}
              </div>
            )}
          </form>

          {/* Shared users list */}
          {sharedUsers.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                People with access
              </h3>
              <div className="space-y-2">
                {sharedUsers.map((share, index) => {
                  // Handle both populated and non-populated user objects
                  const userId = typeof share.user === 'object' ? (share.user._id || share.user.id) : share.user;
                  const username = typeof share.user === 'object' ? share.user.username : 'Unknown';
                  const firstLetter = username ? username[0].toUpperCase() : '?';
                  
                  return (
                    <div
                      key={userId || index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {firstLetter}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium">
                            @{username}
                          </div>
                          <div className="text-xs text-gray-500">
                            {share.permission === 'edit' ? (
                              <span className="flex items-center">
                                <Edit className="w-3 h-3 mr-1" />
                                Can edit
                              </span>
                            ) : (
                              <span className="flex items-center">
                                <Eye className="w-3 h-3 mr-1" />
                                Can view
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveShare(userId)}
                        className="text-gray-400 hover:text-red-600"
                        title="Remove access"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sheet link for sharing */}
          <div className="mt-6 p-3 bg-gray-50 rounded-md">
            <p className="text-xs text-gray-600 mb-1">Share this link:</p>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/sheet/${sheet.id || sheet._id}`}
                className="flex-1 px-2 py-1 text-sm bg-white border border-gray-300 rounded"
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/sheet/${sheet.id || sheet._id}`);
                  setSuccess('Link copied to clipboard!');
                  setTimeout(() => setSuccess(''), 2000);
                }}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;